/**
 * Item 3 (Jun-2026, User-Wunsch) — wiederverwendbares Kundendossier für die
 * Rufnummer-Erkennung (Screen-Pop + manuelle Suche).
 */
import { Phone, Mail, MapPin, Bike, FileText, Clock, User, Hash, PhoneCall } from "lucide-react";
import { formatPrice } from "../../lib/api";

const EVENT_LABELS = {
    dispo_customer_update: "Kundendaten aktualisiert",
    dispo_payment: "Zahlungskonditionen",
    seller_commission_confirm: "Provision bestätigt",
    listing_reserved: "Reserviert",
    listing_sold: "Verkauft",
    handover_protocol: "Übergabeprotokoll",
    work_order_create: "Werkstattauftrag",
};

function CallbackButton({ number }) {
    const tel = (number || "").replace(/[^\d+]/g, "");
    if (!tel) return null;
    return (
        <a href={`tel:${tel}`} className="inline-flex items-center gap-1.5 bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-bold px-3 py-1.5 rounded-sm transition-colors" data-testid="caller-callback-btn">
            <PhoneCall className="w-3.5 h-3.5" /> Zurückrufen
        </a>
    );
}

export default function CallerDossier({ dossier, compact = false }) {
    if (!dossier) return null;
    const c = dossier.customer;
    const callbackNumber = c?.phone || dossier.number;
    return (
        <div className="space-y-3" data-testid="caller-dossier">
            {dossier.matched && c ? (
                <div className="bg-indigo-50 border border-indigo-200 rounded-sm p-3" data-testid="caller-customer">
                    <div className="flex items-center gap-2 mb-1">
                        <User className="w-4 h-4 text-indigo-600" />
                        <span className="font-black text-[#111827]">{c.name}</span>
                        {c.customer_number && <span className="ml-auto text-[10px] font-mono-num bg-white border border-indigo-200 px-1.5 py-0.5 rounded-sm flex items-center gap-1"><Hash className="w-3 h-3" />{c.customer_number}</span>}
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-1 text-xs text-gray-600 mb-2">
                        {c.phone && <span className="flex items-center gap-1.5"><Phone className="w-3.5 h-3.5 text-gray-400" /> {c.phone}</span>}
                        {c.email && <span className="flex items-center gap-1.5 truncate"><Mail className="w-3.5 h-3.5 text-gray-400" /> {c.email}</span>}
                        {c.city && <span className="flex items-center gap-1.5"><MapPin className="w-3.5 h-3.5 text-gray-400" /> {c.city}</span>}
                    </div>
                    <CallbackButton number={callbackNumber} />
                </div>
            ) : (
                <div className="bg-amber-50 border border-amber-200 rounded-sm p-3 text-sm text-amber-800 flex items-center justify-between gap-2" data-testid="caller-no-match">
                    <span>Kein Kunde zu <strong>{dossier.number}</strong> gefunden. (Neukunde / unbekannte Nummer)</span>
                    <CallbackButton number={dossier.number} />
                </div>
            )}

            {dossier.vehicles?.length > 0 && (
                <div data-testid="caller-vehicles">
                    <div className="text-[10px] font-bold uppercase tracking-wider text-gray-500 mb-1 flex items-center gap-1"><Bike className="w-3.5 h-3.5" /> Fahrzeuge / Vorgänge</div>
                    <div className="space-y-1">
                        {dossier.vehicles.map((v) => (
                            <a key={v.id} href={`/edit-listing/${v.id}`} className="flex items-center justify-between bg-white border border-gray-200 rounded-sm px-2.5 py-1.5 text-xs hover:border-indigo-300" data-testid={`caller-vehicle-${v.id}`}>
                                <span className="font-bold text-gray-800 truncate">{v.title}</span>
                                <span className="flex items-center gap-2 shrink-0">
                                    {v.status && <span className="text-[9px] uppercase font-black text-gray-500">{v.status}</span>}
                                    {v.price > 0 && <span className="font-mono-num text-gray-700">{formatPrice(v.price)}</span>}
                                </span>
                            </a>
                        ))}
                    </div>
                </div>
            )}

            {dossier.open_invoices?.length > 0 && (
                <div data-testid="caller-invoices">
                    <div className="text-[10px] font-bold uppercase tracking-wider text-gray-500 mb-1 flex items-center gap-1"><FileText className="w-3.5 h-3.5" /> Offene Rechnungen ({formatPrice(dossier.open_invoices_total)})</div>
                    <div className="space-y-1">
                        {dossier.open_invoices.map((inv) => (
                            <div key={inv.id} className="flex items-center justify-between bg-white border border-gray-200 rounded-sm px-2.5 py-1.5 text-xs">
                                <span>{inv.number || inv.id}</span>
                                <span className="font-mono-num text-red-600">{formatPrice(inv.total_gross)}</span>
                            </div>
                        ))}
                    </div>
                </div>
            )}

            {!compact && dossier.recent_activities?.length > 0 && (
                <div data-testid="caller-activities">
                    <div className="text-[10px] font-bold uppercase tracking-wider text-gray-500 mb-1 flex items-center gap-1"><Clock className="w-3.5 h-3.5" /> Letzte Aktivitäten</div>
                    <div className="space-y-0.5">
                        {dossier.recent_activities.map((a, i) => (
                            <div key={i} className="flex items-center justify-between text-[11px] text-gray-500">
                                <span>{EVENT_LABELS[a.event_type] || a.event_type}</span>
                                <span>{a.created_at ? new Date(a.created_at).toLocaleDateString("de-DE") : ""}</span>
                            </div>
                        ))}
                    </div>
                </div>
            )}
        </div>
    );
}
